import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { WebhookIntegrationRecord } from "../WebhookIntegrationRecord";

export function useFoxgloveIntegration(orgId?: string) {
  const { integrationService } = useDomainServices();
  const queryClient = useQueryClient();

  return useQuery<WebhookIntegrationRecord | null, Error>({
    queryKey: ["foxgloveIntegration", orgId],
    queryFn: async ({ signal }) => {
      if (orgId === undefined) {
        throw new Error("Invalid orgId");
      }

      // The generic "get webhook integrations by provider" API might return multiple
      // values. For Foxglove specifically, we only ever expect to see one or none.
      //
      // If any are returned, we'll cache all of their records, and return the first one (which
      // should be the only one). If none are returned, we'll return null.
      const data = await integrationService.getWebhooksIntegrationByProvider(
        "foxglove",
        orgId,
        { abortSignal: signal },
      );

      if (data.items.length === 0) {
        return null;
      }

      data.items.forEach((item) => {
        queryClient.setQueryData(
          ["webhookEndpointId", item.webhook_endpoint_id],
          item,
        );
      });

      return data.items[0];
    },
    enabled: orgId !== undefined,
    staleTime: 60000,
  });
}
